"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Plus, Minus } from "lucide-react";
import { SocialMediaIcons } from "@/components/shared/SocialMediaIcons";
import { PayloadLink } from "@/components/shared/PayloadLink";
import { GridGroupShell } from "@/payload/blocks/grid-sections/GridGroupShell";
import { GridSectionCrosshairs } from "@/payload/blocks/grid-sections/GridSectionCrosshairs";
import { transformFooterData } from "@/lib/footer-utils";
import { buildFooterSocialIcons } from "@/lib/social-links";
import type { Footer, Media, Setting } from "@/payload-types";
import { siteConfig } from "@/lib/site.config";
import { cn } from "@/lib/utils";

interface FooterMainProps {
  footer: Footer;
  settings: Setting | null;
}

const LINE_COLOR = "border-white/15";
const LINE_CSS_COLOR = "color-mix(in oklab, white 15%, transparent)";
const GLOW_COLOR = "border-white/12";

export function FooterMain({ footer, settings }: FooterMainProps) {
  const footerData = transformFooterData(footer);
  if (!footerData) return null;

  const {
    companyInfo,
    navigationColumns,
    navigationColumnsRight,
    accreditations,
  } = footerData;
  const socialMedia = buildFooterSocialIcons(settings?.socialProfiles);

  const logoImage =
    companyInfo?.logo && typeof companyInfo.logo === "object"
      ? (companyInfo.logo as Media)
      : null;

  return (
    <GridGroupShell glowColor={GLOW_COLOR}>
      <section
        className={cn(
          "relative w-full bg-black text-white grid-decorative-verticals",
          `border-x ${LINE_COLOR}`,
          `border-b ${LINE_COLOR}`
        )}
        style={{ ["--grid-line-color" as string]: LINE_CSS_COLOR }}
      >
        {/* Cell content grid */}
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3">
          {/* Left column — logo + tagline (top), accreditations + social (bottom) */}
          <div className="decorative-grid-cell grid-cell lg:border-t-0 col-span-1 min-h-48 aspect-square 2xl:aspect-[4/3]">
            <div className="flex flex-col justify-between h-full p-3 sm:p-4 lg:p-5">
              <div>
                {logoImage?.url ? (
                  <Image
                    src={logoImage.url}
                    alt={logoImage.alt || companyInfo?.brandName || siteConfig.name}
                    width={360}
                    height={90}
                    className="w-[160px] xl:w-[360px] h-auto object-contain object-left"
                    sizes="(min-width: 1280px) 360px, 160px"
                    priority={false}
                  />
                ) : (
                  <h2 className="font-subheading text-3xl lg:text-4xl 2xl:text-5xl text-white">
                    {companyInfo?.brandName || siteConfig.name}
                  </h2>
                )}
              </div>

              <div className="flex flex-col gap-6 mt-8">
                <div className="flex flex-wrap items-end gap-6">
                  {accreditations?.map((item, index) => {
                    const widthPx = item.width ?? 120;

                    // BlockMark verification widget — self-contained badge, tooltip and link to the certificate.
                    // The widget reserves ~32px beside the mark for its tick, so it's floored at 96px on mobile.
                    if (item.type === "widget") {
                      if (!item.widgetUrl) return null;
                      return (
                        <iframe
                          key={item.id || index}
                          src={item.widgetUrl}
                          title={item.name}
                          loading="lazy"
                          style={{ ["--badge-w" as string]: `${widthPx}px` }}
                          className="block flex-shrink-0 border-0 aspect-square w-[max(96px,calc(var(--badge-w)*0.5))] sm:w-[var(--badge-w)] max-w-full"
                        />
                      );
                    }

                    const badge = item.badge as Media;
                    if (!badge?.url) return null;

                    const imageElement = (
                      // eslint-disable-next-line @next/next/no-img-element -- editor-sized accreditation logo; the browser must derive height from the image's own aspect ratio, which next/image can't do without known dimensions (often null for SVG uploads)
                      <img
                        src={badge.url ?? ""}
                        alt={item.name}
                        style={{ ["--badge-w" as string]: `${widthPx}px` }}
                        className="h-auto w-[calc(var(--badge-w)*0.5)] sm:w-[var(--badge-w)] max-w-full object-contain opacity-90 hover:opacity-100 transition-opacity"
                        loading="lazy"
                      />
                    );

                    return item.url ? (
                      <Link
                        key={item.id || index}
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-shrink-0"
                      >
                        {imageElement}
                      </Link>
                    ) : (
                      <div key={item.id || index} className="flex-shrink-0">
                        {imageElement}
                      </div>
                    );
                  })}
                </div>

                <SocialMediaIcons links={socialMedia} className="flex space-x-3 lg:hidden" />
              </div>
            </div>
          </div>

          {/* Mobile — combined accordion (left + right columns merged into one block) */}
          <div className="decorative-grid-cell grid-cell lg:border-t-0 col-span-1 min-h-48 aspect-square 2xl:aspect-[4/3] lg:hidden">
            <div className="h-full p-3 sm:p-4">
              <FooterAccordionNav
                columns={[
                  ...(navigationColumns ?? []),
                  ...(navigationColumnsRight ?? []),
                ]}
              />
            </div>
          </div>

          {/* Desktop — middle column (left list) */}
          <div className="decorative-grid-cell grid-cell lg:border-t-0 col-span-1 min-h-48 aspect-square 2xl:aspect-[4/3] hidden lg:block">
            <div className="h-full p-3 sm:p-4 lg:p-5">
              <FooterAccordionNav columns={navigationColumns} />
            </div>
          </div>

          {/* Desktop — right column (right list) */}
          <div className="decorative-grid-cell grid-cell lg:border-t-0 col-span-1 min-h-48 aspect-square 2xl:aspect-[4/3] hidden lg:block">
            <div className="h-full p-3 sm:p-4 lg:p-5">
              <FooterAccordionNav columns={navigationColumnsRight} />
              <SocialMediaIcons links={socialMedia} className="flex space-x-3 pt-6" />
            </div>
          </div>
        </div>
        <GridSectionCrosshairs />
      </section>
    </GridGroupShell>
  );
}

/* ------------------------------------------------------------------ */
/* Accordion navigation for the middle column                         */
/* ------------------------------------------------------------------ */

function FooterAccordionNav({
  columns,
}: {
  columns:
    | NonNullable<ReturnType<typeof transformFooterData>>["navigationColumns"]
    | undefined;
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  if (!columns || columns.length === 0) return null;

  return (
    <div className="border-t border-white/20">
      {columns.map((column, index) => {
        const isOpen = openIndex === index;
        const hasChildren = (column.links?.length ?? 0) > 0;
        const titleLinkType = column.titleLink?.type;
        const hasTitleLink =
          titleLinkType === "page" || titleLinkType === "custom";
        const titleClassName =
          "font-heading font-semibold text-lg text-white text-left";

        const titleNode = hasTitleLink ? (
          <PayloadLink
            type={titleLinkType}
            page={
              typeof column.titleLink?.page === "number"
                ? null
                : column.titleLink?.page
            }
            url={column.titleLink?.url}
            newTab={column.titleLink?.newTab}
            className={cn(titleClassName, "hover:text-white/80 transition-colors")}
          >
            {column.title}
          </PayloadLink>
        ) : (
          <span className={titleClassName}>{column.title}</span>
        );

        return (
          <div key={index} className="border-b border-white/20">
            {hasChildren ? (
              <div className="flex w-full items-center justify-between py-3">
                {titleNode}
                <button
                  onClick={() => toggle(index)}
                  type="button"
                  aria-expanded={isOpen}
                  aria-label={isOpen ? "Collapse" : "Expand"}
                  className="-mr-2 p-2"
                >
                  {isOpen ? (
                    <Minus className="h-5 w-5 text-white/70 shrink-0" />
                  ) : (
                    <Plus className="h-5 w-5 text-white/70 shrink-0" />
                  )}
                </button>
              </div>
            ) : (
              <div className="flex w-full items-center py-3">{titleNode}</div>
            )}

            {hasChildren && (
              <div
                className={cn(
                  "overflow-hidden transition-all duration-300 ease-in-out",
                  isOpen ? "max-h-96 pb-3" : "max-h-0"
                )}
              >
                <ul className="space-y-2">
                  {column.links?.map((link, linkIndex) => (
                    <li key={link.id || linkIndex}>
                      <PayloadLink
                        type={link.linkType}
                        page={
                          typeof link.page === "number" ? null : link.page
                        }
                        url={link.url}
                        className="text-sm text-white/70 hover:text-white transition-colors"
                      >
                        {link.label}
                      </PayloadLink>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
